<template lang="pug">
  b-modal(
    v-model="show",
    header-class="lmm-common-popup-header",
    :title="$t('mdm_apply_config')",
    no-close-on-backdrop)
    div
      p.mb-2 {{$t('mdm_apply_config_desc')}}
      // 적용 대상 스코프 — 단건=기기명·OS / 일괄=대수 + OS 구성
      div.small.mb-2
        strong {{$t('mdm_apply_target')}}
        span.ml-1(v-if="targets.length === 1") {{ targets[0].alias }} · {{ osLabelOf(targets[0]) }}
        span.ml-1(v-else) {{$t('mdm_apply_target_count', { count: targets.length })}}
        span.ml-1.text-muted(v-if="targets.length > 1") ({{ osBreakdownText }})
      b-form-select(
        v-model="profileFk",
        :options="profileOptions",
        size="sm")
      // 선택 구성 기준 적용/제외 + 제외 사유
      div.mt-2.small(v-if="profileFk")
        div.text-muted {{$t('mdm_apply_summary', { apply: preview.apply, skip: preview.skip })}}
        div.text-muted(v-if="preview.skip") {{$t('mdm_apply_skip_reason', { same: preview.skipSame, os: preview.skipOs })}}
    template(slot="modal-footer")
      b-btn(size="sm", variant="primary", :disabled="!preview.apply", @click="submit") {{$t('submit')}}
      b-btn(size="sm", @click="show = false") {{$t('cancel')}}
</template>

<script>
// 원격기기에 MDM 구성(기기정책)을 적용하는 모달 — 단건·일괄 공용.
// 기기목록 화면(device)에서 ref로 open(targets) 호출, 적용 완료 시 "applied" emit → 부모가 목록 재로드.
import _ from "lodash"
import { MdmProfileAPI, MdmAssignAPI } from "@/api/mdm"

// 구성 os_type → 표시 라벨 (브랜드명이라 번역 안 함)
const OS_LABEL = { android: "Android", windows: "Windows", osx: "macOS" }

export default {
  name: "MdmAssignModal",
  data() {
    return {
      show: false,
      profileFk: null,
      profiles: [], // 회사 전체 구성(모든 OS)
      targets: [], // 대상 기기 객체 배열(일괄=선택분, 단건=[dev])
    }
  },
  computed: {
    // 구성 드롭다운 옵션 — 대상 기기 OS가 단일이면 그 OS 구성만, 섞였으면 전체를 OS 라벨과 함께 노출
    profileOptions() {
      const targetOs = new Set(this.targets.map(d => this.osIdToType(d.dev_os_id)).filter(Boolean))
      let list = this.profiles
      if (targetOs.size === 1) {
        const only = [...targetOs][0]
        list = list.filter(p => p.os_type === only)
      }
      // 단건이면 현재 적용 구성 옵션 뒤에 "(현재 적용됨)" 표시 — 기본 선택된 게 무엇인지 알림.
      const currentName = this.targets.length === 1 ? this.targets[0].profile_name : null
      const options = _.chain(list)
        .sortBy(["os_type", "name"])
        .map(p => {
          const base = `${p.name} · ${OS_LABEL[p.os_type] || p.os_type}`
          const isCurrent = currentName && p.name === currentName
          return { value: p.pk, text: isCurrent ? `${base} ${this.$t("mdm_config_current_applied")}` : base }
        })
        .value()
      // 단건+현재 구성 있으면 open()에서 그 구성을 기본 선택 → placeholder 없이 목록만.
      // 미할당(단건) 또는 일괄이면 기본 선택할 현재값이 없으니 "구성 선택" placeholder를 앞에 둔다.
      const hasCurrent = !!currentName
      if (hasCurrent) return options
      return [{ value: null, text: this.$t("mdm_apply_config_placeholder"), disabled: true }, ...options]
    },
    // 선택한 구성 기준 적용/제외 미리보기 — 제외 사유 분해(이미 동일 / OS 불일치)
    preview() {
      const profile = this.profiles.find(p => p.pk === this.profileFk)
      if (!profile) return { apply: 0, skip: this.targets.length, skipSame: 0, skipOs: 0 }
      let apply = 0
      let skipSame = 0
      let skipOs = 0
      this.targets.forEach(d => {
        if (this.osIdToType(d.dev_os_id) !== profile.os_type) skipOs++
        else if (d.profile_name === profile.name) skipSame++
        else apply++
      })
      return { apply, skip: skipSame + skipOs, skipSame, skipOs }
    },
    // 대상 기기 OS 구성 요약 (일괄 시 "Android 3 · Windows 1")
    osBreakdownText() {
      const counts = {}
      this.targets.forEach(d => {
        const label = this.osLabelOf(d)
        counts[label] = (counts[label] || 0) + 1
      })
      return Object.entries(counts)
        .map(([label, n]) => `${label} ${n}`)
        .join(" · ")
    },
  },
  methods: {
    // dev_os_id(1=Win·2=Linux·3=Mac·4=Android) → 구성 os_type. Linux는 MDM 미지원(null)
    osIdToType(devOsId) {
      return { 1: "windows", 3: "osx", 4: "android" }[devOsId] || null
    },
    // 기기 OS 표시 라벨 (미지원 OS는 "미지원")
    osLabelOf(dev) {
      const type = this.osIdToType(dev.dev_os_id)
      return type ? OS_LABEL[type] : this.$t("mdm_os_unsupported")
    },
    // 대상 기기 배열로 모달 열기 — 열 때마다 구성 목록을 새로 로드(stale 방지)
    async open(targets) {
      if (!targets.length) {
        this.$awn.warning(this.$t("no-selects"))
        return
      }
      this.targets = targets
      this.profileFk = null
      this.show = true
      try {
        const res = await MdmProfileAPI.get("")
        this.profiles = res.data.objects || []
      } catch (e) {
        this.profiles = [] // 실패 토스트는 전역 인터셉터가 처리
      }
      // 단건 + 현재 적용 구성 있으면 그 구성을 기본 선택 (드롭다운이 현재 상태를 그대로 보여줌).
      // 미할당·일괄은 기본 선택 없음 → placeholder 유지.
      if (this.targets.length === 1 && this.targets[0].profile_name) {
        const current = this.profiles.find(p => p.name === this.targets[0].profile_name)
        if (current) this.profileFk = current.pk
      }
    },
    // 구성 적용 실행 — OS 일치 + 이미 같은 구성 아닌 기기만, 단일 벌크 호출
    async submit() {
      const profile = this.profiles.find(p => p.pk === this.profileFk)
      if (!profile) {
        this.$awn.warning(this.$t("mdm_apply_config_placeholder"))
        return
      }
      // OS 불일치·동일 구성은 사전 제외 (요약과 동일 기준 — 적용 대상만 전송)
      const targets = this.targets.filter(
        d => this.osIdToType(d.dev_os_id) === profile.os_type && d.profile_name !== profile.name,
      )
      if (!targets.length) {
        this.AppState.$emit("show-message-dialog", { context: { path: "mdm_apply_none" } })
        return
      }
      try {
        // 단일 벌크 호출 → 서버가 단일 트랜잭션으로 처리(기기당 개별 PUT 팬아웃 딥락 없음)
        const res = await MdmAssignAPI.put("", {
          device_pks: targets.map(d => d.pk),
          profile_fk: profile.pk,
        })
        const applied = res.data.objects.applied || []
        // 선택 전체 중 실제 적용 수 — 나머지는 제외(OS 불일치·동일 구성). 프리뷰와 일관되게 노출
        const excluded = this.targets.length - applied.length
        this.$awn.success(
          excluded > 0
            ? this.$t("mdm_apply_done_excluded", { success: applied.length, excluded })
            : this.$t("mdm_apply_done", { success: applied.length }),
        )
        this.show = false
        // 부모가 목록 재로드 — 서버가 실제 적용한 기기 pk·구성명 전달(낙관적 갱신용)
        this.$emit("applied", { pks: applied, profileName: profile.name })
      } catch (e) {
        // 에러 토스트는 전역 인터셉터가 처리
      }
    },
  },
}
</script>
